/**
 * The two chips and the sort control for the Candidate Requests queue.
 *
 * The chips are FILTERS, not decoration: each one shows the queue it counts, and clicking the active
 * one clears it. The counts come from the same predicates the filter uses (`requestGroups.ts`), so a
 * chip and the list it filters to cannot disagree.
 *
 * Reduced to two on Sofia's request (Sep 10 2026): **Unread** — the default view, mail the recruiter
 * has not opened — and **Inbound questions**, the whole queue. The other buckets ("Ready to send",
 * "Need you", "Scheduled", "No reply needed") were more chips than a recruiter reads; they remain
 * valid `?bucket=` values (`requestGroups.isRequestBucket`) so old links still filter, but the bar
 * no longer offers them. Both chips are always rendered, at zero too: with Unread as the default the
 * active filter must stay clearable even when nothing is unread.
 *
 * Its own component because RequestsTab sits at its 500-line ceiling.
 */
import { COLORS } from './format';
import { FilterChip } from './RoleAccordion';
import { SORT_OPTIONS, type RequestBucket, type RequestSort } from './requestGroups';

export function RequestFilterBar({
  total,
  unreadCount,
  bucket,
  sort,
  onToggleBucket,
  onSort,
}: {
  total: number;
  unreadCount: number;
  bucket: RequestBucket;
  sort: RequestSort;
  onToggleBucket: (b: RequestBucket) => void;
  onSort: (s: RequestSort) => void;
}) {
  return (
    <>
      <FilterChip
        label="Unread"
        count={unreadCount}
        dot={COLORS.statusOrange}
        active={bucket === 'unread'}
        onClick={() => onToggleBucket('unread')}
      />
      <FilterChip
        label={total === 1 ? 'Inbound question' : 'Inbound questions'}
        count={total}
        active={bucket === 'all'}
        onClick={() => onToggleBucket('all')}
      />
      <label className="ml-1 inline-flex items-center gap-1.5 text-xs text-gray-500">
        Sort
        <select
          value={sort}
          onChange={(e) => onSort(e.target.value as RequestSort)}
          className="rounded-lg border border-gray-300 px-2 py-1 text-xs"
          aria-label="Sort requests"
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    </>
  );
}
